import {
  useCallback,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import * as MenuPrimitive from "@radix-ui/react-dropdown-menu";

import { cx } from "@/lib/cx";
import { Button } from "@/ui/Button";
import { ROW_RECIPE } from "../list";
import { CheckIcon, EllipsisIcon } from "@/ui/icons";
import { LAYER } from "./surface";
import { useBackClose } from "./useBackClose";
import { useCloseOnlyOutside } from "./useCloseOnlyOutside";

/**
 * The contextual menu — right-click, long-press, or the always-visible `⋯`.
 *
 * The app had none, and UI-REVIEW is blunt about the consequence: every
 * secondary action ended up on the row itself, which is why a task row carried
 * Tomorrow / Edit / Delete at 32px each and hid all three behind `:hover` —
 * where a finger can never find them (P7). One always-visible `⋯` that opens a
 * real menu is both smaller at rest and reachable on touch.
 *
 * The shared menu is deliberately denser on desktop while preserving a 44px
 * coarse-pointer target:
 *
 * - **compact rows on the shared row recipe**, with no divider between related commands.
 * - **the icon is on the leading edge** so commands scan like every other
 *   Material list in the app.
 * - **groups are separated by the shared hairline**, so "the
 *   destructive one is on its own" is legible without reading it.
 * - an opaque raised surface + `rounded-surface` + `squircle`, no border — the
 *   shadow does the separating without letting page text show through.
 *
 * `@radix-ui/react-dropdown-menu` still brings roving tabindex, type-ahead,
 * Escape, outside-click and correct `aria-*` wiring.
 */

/* -------------------------------------------------------------------------- */

/**
 * The surface every transient popper in the app shares.
 *
 * Exported from here rather than from `surface.ts` because that module is not
 * this phase's to edit; `Popover`, `Select` and `Menu` import it so the three
 * cannot drift into three different radii again.
 *
 * A popper must stay legible over dense cards and labels, including in Android
 * WebView where backdrop compositing varies by device. `bg-raised` is therefore
 * deliberately opaque; elevation comes from `shadow-pop`, not transparency.
 * No border. `rounded-surface` resolves to the shared 12px surface radius;
 * `squircle` makes the corner continuous wherever `corner-shape` exists.
 */
export const popperMaterial = cx(
  "busee-popper overflow-hidden rounded-surface border-2 border-ps1-orange bg-raised text-ink shadow-pop",
  "origin-(--radix-popper-transform-origin)",
  // Radix measures the trigger and the viewport for us; without these the
  // content of a long menu simply overflows the screen.
  "max-h-(--radix-popper-available-height) overflow-y-auto",
);

/**
 * One row of a contextual menu or a select list.
 *
 * Structure, not colour — every tone brings its own `data-highlighted` pair (see
 * {@link MENU_TONES} for why they cannot live here).
 *
 * Desktop rows use the 40px recipe; touch and pen retain the 44px minimum for
 * Delete / Rename / Sign out. Labels use the product control-label step rather
 * than body copy so the menu stays dense without becoming cramped.
 *
 * Radix owns roving focus and pointer highlighting. The highlighted row uses
 * the same hover fill as any other row; keyboard focus still receives the one
 * global accent outline from `index.css`.
 *
 * Context-menu rows intentionally do not use `data-busee-menu-row`: that hook
 * belongs to select-style lists which still draw row separators.
 */
export const menuRow = cx(
  "relative flex w-full cursor-default items-center gap-3 rounded-control px-3 text-label select-none",
  ROW_RECIPE,
  "data-disabled:pointer-events-none data-disabled:opacity-45",
);

export type MenuItemTone = "default" | "danger";

/**
 * A row's *colours* under the keyboard/hover cursor, one complete set per tone.
 *
 * Deliberately not folded into {@link menuRow}. Tailwind orders utilities by
 * kind, not by the order they are written, so a base carrying
 * `data-highlighted:bg-hover data-highlighted:text-ink` and a tone carrying
 * `data-highlighted:bg-danger-soft data-highlighted:text-danger` are four rules
 * at identical specificity, and the winner is whichever the framework happened
 * to emit last. In the built stylesheet that was the base both times — so a
 * highlighted Delete row rendered with the neutral hover fill *and* lost its red
 * label entirely. Splitting them means the two sets never meet.
 *
 * Any new tone must bring its own pair. There is no shared fallback on purpose.
 */
export const MENU_TONES: Record<MenuItemTone, string> = {
  default: "text-ink data-highlighted:bg-ps1-gold-fill data-highlighted:text-ps1-gold-ink",
  // Red on the label, not a red fill: the item is dangerous, not selected.
  danger: "text-danger data-highlighted:bg-danger-soft data-highlighted:text-danger",
};

/* -------------------------------------------------------------------------- */

export type MenuProps = {
  /** Must be a single element that can take a ref — Radix clones it. */
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  /** Accessible name for the menu surface itself. */
  label?: string;
  /**
   * Blocks the page behind the menu. Radix's default, and right for a menu
   * opened from a button; pass `false` for one opened by long-press on a row
   * inside a scroller, where locking the scroll is worse than an outside click.
   */
  modal?: boolean;
  className?: string;
};

export function Menu(props: MenuProps) {
  // Controllable here rather than left to Radix, because two closers outside
  // Radix need to know whether the menu is open: the phone's Back button, and
  // the outside-click rule below.
  const { open, onOpenChange, modal = true } = props;
  const [uncontrolled, setUncontrolled] = useState(false);
  const isOpen = open ?? uncontrolled;
  const setOpen = (next: boolean) => {
    if (open === undefined) {
      setUncontrolled(next);
    }
    onOpenChange?.(next);
  };
  // A modal menu is closed-only-on-outside-click by Radix already (the page
  // behind it ignores the pointer). A non-modal one leaves the page live, so it
  // gets the same behaviour from useCloseOnlyOutside.
  const nothing = useRef<Element>(null);
  useCloseOnlyOutside(isOpen && !modal, () => setOpen(false), nothing);
  useBackClose(isOpen, () => setOpen(false));
  return menuTree({ ...props, open: isOpen, onOpenChange: setOpen });
}

/** The Radix tree {@link Menu} renders; hook-free, so a test can inspect it. */
export function menuTree({
  trigger,
  children,
  open,
  onOpenChange,
  side = "bottom",
  align = "end",
  sideOffset = 6,
  label,
  modal = true,
  className,
}: MenuProps) {
  return (
    <MenuPrimitive.Root open={open} onOpenChange={onOpenChange} modal={modal}>
      <MenuPrimitive.Trigger asChild>{trigger}</MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          aria-label={label}
          // One compact inset keeps the keyboard outline off the clipped edge.
          // 12rem keeps common actions on one line without turning a short
          // context menu into a side panel. Callers can still widen it.
          className={cx(popperMaterial, LAYER.overlay, "min-w-48 p-1", className)}
        >
          {children}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}

export type MenuItemProps = {
  onSelect: () => void;
  children: ReactNode;
  tone?: MenuItemTone;
  /** Drawn on the leading edge. 20px, `currentColor`. */
  icon?: ReactNode;
  /** Right-aligned keyboard hint. Purely a label — bind the key yourself. */
  shortcut?: string;
  disabled?: boolean;
  /** Keeps the menu open after the click, e.g. for a toggle. */
  keepOpen?: boolean;
};

export function MenuItem({
  onSelect,
  children,
  tone = "default",
  icon,
  shortcut,
  disabled = false,
  keepOpen = false,
}: MenuItemProps) {
  return (
    <MenuPrimitive.Item
      data-busee-context-menu-row=""
      disabled={disabled}
      onSelect={(event) => {
        if (keepOpen) {
          event.preventDefault();
        }
        onSelect();
      }}
      className={cx(menuRow, MENU_TONES[tone])}
    >
      <span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {/* `text-subtle`, not `text-faint`: a keyboard hint is text, and faint is
          3.55:1 in light. It is the quiet end of the text scale, not below it. */}
      {shortcut ? (
        <span className="numeric shrink-0 text-caption text-subtle">{shortcut}</span>
      ) : null}
    </MenuPrimitive.Item>
  );
}

export type MenuCheckboxItemProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
};

/**
 * A toggle inside a menu. The tick is on the leading edge and is the **vivid**
 * accent, not the text teal: it is a mark, not a label, so it takes `bg-accent`'s
 * family rather than `accent-ink`'s (IOS-DESIGN §1.1).
 */
export function MenuCheckboxItem({
  checked,
  onCheckedChange,
  children,
  disabled = false,
}: MenuCheckboxItemProps) {
  return (
    <MenuPrimitive.CheckboxItem
      data-busee-context-menu-row=""
      checked={checked}
      onCheckedChange={onCheckedChange}
      onSelect={(event) => event.preventDefault()}
      disabled={disabled}
      className={cx(menuRow, MENU_TONES.default)}
    >
      {/* 20px. `index.css` rule 2 puts the vivid accent's floor at a 20px mark;
          the drawn glyph is what is measured, not the box around it. The
          identical indicator in `Toast` is `size-5` for the same reason. */}
      <span className="flex size-5 shrink-0 items-center justify-center text-accent-ink">
        <MenuPrimitive.ItemIndicator>
          <CheckIcon className="size-5" />
        </MenuPrimitive.ItemIndicator>
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </MenuPrimitive.CheckboxItem>
  );
}

/**
 * The break between two groups of actions.
 *
 * A compact hairline break appears only when a caller explicitly separates
 * command categories. Related commands have no line between them.
 */
export function MenuSeparator() {
  return <MenuPrimitive.Separator data-busee-menu-divider="" className="mx-3 h-px bg-separator" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <MenuPrimitive.Label
      data-busee-menu-label=""
      className="px-3 pt-3 pb-2 text-caption font-semibold tracking-wide text-subtle"
    >
      {children}
    </MenuPrimitive.Label>
  );
}

/* -------------------------------------------------------------------------- */

export type OverflowItem = {
  label: string;
  onSelect: () => void;
  tone?: MenuItemTone;
  disabled?: boolean;
  /** Leading glyph. Use the 20×20 stroke glyphs from the shared icon module. */
  icon?: ReactNode;
  /** Draws a group break above this item. */
  separated?: boolean;
};

export type OverflowMenuProps = {
  items: OverflowItem[];
  /** Accessible name of the trigger. */
  label?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /**
   * Controlled open state. Pass the pair from {@link useContextMenu} to have
   * right-click and long-press on the surrounding row open this same menu.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  className?: string;
};

/**
 * The `⋯` button plus its menu, as one thing.
 *
 * This is the row's action slot, the peek header's action slot and the dialog
 * footer's destructive slot, so it is worth having in one place: Delete should
 * look and behave identically wherever it is reached from (UI-REVIEW P11).
 *
 * The trigger is a plain tinted glyph at rest — `bg-fill-3` only while the menu
 * is open, so a list of rows is not a column of grey squares.
 */
export function OverflowMenu({
  items,
  label = "More actions",
  side = "bottom",
  align = "start",
  open,
  onOpenChange,
  modal,
  className,
}: OverflowMenuProps) {
  return (
    <Menu
      side={side}
      align={align}
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={label}
          className={cx("data-[state=open]:bg-fill-3 data-[state=open]:text-ink", className)}
        >
          <EllipsisIcon className="size-5" />
        </Button>
      }
    >
      {items.map((item) => (
        <MenuGroupedItem key={item.label} item={item} />
      ))}
    </Menu>
  );
}

function MenuGroupedItem({ item }: { item: OverflowItem }) {
  return (
    <>
      {item.separated || item.tone === "danger" ? <MenuSeparator /> : null}
      <MenuItem
        onSelect={item.onSelect}
        tone={item.tone}
        icon={item.icon}
        disabled={item.disabled}
      >
        {item.label}
      </MenuItem>
    </>
  );
}

/* -------------------------------------------------------------------------- */

/** iOS opens a contextual menu at 0.5s. Matching it is the whole point. */
const LONG_PRESS_MS = 500;
/** A press that travels more than this is a scroll, not a long-press. */
const LONG_PRESS_SLOP_PX = 10;

export type ContextMenuTrigger = {
  open: boolean;
  setOpen: (open: boolean) => void;
  /**
   * Spread onto the row that should respond. It is *not* the menu's trigger —
   * the `⋯` button stays that, so the popper still has something to anchor to
   * and the keyboard still has something to tab to.
   *
   * `onClickCapture` is the one that is not a gesture: a touch long-press is a
   * single gesture that emits `pointerdown` … 500ms … `pointerup`, and the
   * browser then synthesises a **`click`** on whatever is under the finger —
   * which by then is the row behind the menu that just opened. Unguarded, the
   * gesture reads as "open the menu, then immediately fire the row": on a task
   * that opened the rename editor, and on a workout row, whose whole surface is
   * a `role="checkbox"`, it ticked the exercise.
   *
   * It swallows every click that **landed on this row** while the menu is open,
   * rather than trying to identify the one. With the menu up the row behind it
   * is not the active surface, so none of its controls should fire; and Radix
   * toggles its own trigger on `pointerdown`, so closing by tapping `⋯` still
   * works.
   *
   * "Landed on this row" used to read "every click", and that was a real bug
   * rather than a nuance: a React portal propagates through the React tree, so
   * the menu's own items are also `onClickCapture`'s children and **no menu item
   * on a task row ever fired**. See the handler for the containment check that
   * fixed it.
   */
  triggerProps: {
    onContextMenu: (event: ReactMouseEvent) => void;
    onPointerDown: (event: ReactPointerEvent) => void;
    onPointerMove: (event: ReactPointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onPointerLeave: () => void;
    onClickCapture: (event: ReactMouseEvent) => void;
  };
};

/**
 * Right-click and long-press, both opening the row's own `⋯` menu.
 *
 * ```tsx
 * const menu = useContextMenu();
 * <li {...menu.triggerProps}>
 *   …
 *   <OverflowMenu items={items} open={menu.open} onOpenChange={menu.setOpen} />
 * </li>
 * ```
 *
 * Written here rather than taken from `@radix-ui/react-context-menu`, which is
 * not a dependency of this app and would have to be for one gesture.
 *
 * Two details that are the difference between this and a nuisance:
 *
 * - **The timer only runs for touch and pen.** A mouse gets `contextmenu`, which
 *   fires at the right moment already; starting a 500ms timer on a mouse press
 *   would open a menu on every slow click.
 * - **Movement cancels.** A finger that travels 10px is starting a scroll on a
 *   task list or a drag on a board, and a menu that steals that is worse than no
 *   menu at all. `onPointerCancel` covers the case where the browser itself
 *   claims the gesture for scrolling.
 * - **The click that ends a long-press is swallowed.** See {@link ContextMenuTrigger}'s
 *   `onClickCapture` — this is in the hook rather than in each caller because
 *   every surface that adopts long-press hits it, and the first two both did.
 */
export function useContextMenu(): ContextMenuTrigger {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    origin.current = null;
  }, []);

  return {
    open,
    setOpen: useCallback(
      (next: boolean) => {
        clear();
        setOpen(next);
      },
      [clear],
    ),
    triggerProps: {
      onContextMenu: (event) => {
        // Suppress the browser's own menu only because this one replaces it;
        // without the preventDefault both would appear on a right-click.
        event.preventDefault();
        clear();
        setOpen(true);
      },
      onPointerDown: (event) => {
        if (event.pointerType === "mouse") {
          return;
        }
        clear();
        origin.current = { x: event.clientX, y: event.clientY };
        timer.current = window.setTimeout(() => {
          timer.current = null;
          setOpen(true);
        }, LONG_PRESS_MS);
      },
      onPointerMove: (event) => {
        const from = origin.current;
        if (!from) {
          return;
        }
        const travelled =
          Math.abs(event.clientX - from.x) + Math.abs(event.clientY - from.y);
        if (travelled > LONG_PRESS_SLOP_PX) {
          clear();
        }
      },
      onPointerUp: clear,
      onPointerCancel: clear,
      onPointerLeave: clear,
      onClickCapture: (event) => {
        if (!open) {
          return;
        }
        // Only clicks that really landed on this row.
        //
        // Radix portals the menu into `<body>`, but a **React portal still
        // propagates events through the React tree**, and the menu is a React
        // child of the row that renders it. So an item click arrives here — in
        // the capture phase, i.e. *before* the item's own handler — and
        // swallowing it unconditionally meant no item on a task row's `⋯` menu
        // ever fired: the menu simply stayed open. (Observed: menu still
        // mounted 2s after clicking Rename, no editor, no state change.)
        //
        // The DOM containment check separates the two cases exactly. The
        // long-press click this guard exists for is synthesised on the row
        // itself and is a DOM descendant; anything arriving through the portal
        // is not.
        const target = event.target as Node | null;
        if (target && event.currentTarget.contains(target)) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
    },
  };
}
